import React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { cn } from '@/utils/styles';

interface DropdownOption {
  label: string;
  value: string;
}

interface DropdownOptionGroup {
  label: string;
  options: DropdownOption[];
}

interface DropdownProps<T> extends VariantProps<typeof triggerVariants> {
  options: DropdownOption[] | DropdownOptionGroup[];
  value: string;
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  selectContentProps?: React.ComponentPropsWithoutRef<typeof SelectContent>;
}

const triggerVariants = cva('w-auto', {
  variants: {
    variant: {
      default: '',
      light: 'border-neutral-700 bg-neutral-800 text-neutral-0',
      neutral: 'border-neutral-700 bg-neutral-800 text-neutral-0',
      transparent: 'border-none bg-transparent',
    },
    size: {
      default: '',
      lg: 'p-3',
      xl: 'px-4 py-3',
      none: 'p-0 text-xs',
    },
  },
});

const contentVariants = cva(
  'border-neutral-700 bg-neutral-800 text-neutral-0',
  {
    variants: {
      variant: {
        default: '',
        neutral: '',
        transparent: '',
        light: 'border-neutral-700 bg-neutral-800 text-neutral-0',
      },
    },
  },
);

const Dropdown = <T extends string>({
  options,
  value,
  onChange,
  placeholder,
  className,
  variant = 'default',
  size = 'default',
  selectContentProps,
}: DropdownProps<T>) => {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className={cn(triggerVariants({ variant, size }), className)}>
        <SelectValue placeholder={placeholder} className="w-auto" />
      </SelectTrigger>
      <SelectContent
        className={cn(contentVariants({ variant }))}
        {...selectContentProps}>
        {options.map(option =>
          'options' in option ? (
            <SelectGroup key={option.label}>
              <SelectLabel>{option.label}</SelectLabel>
              {option.options.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ),
        )}
      </SelectContent>
    </Select>
  );
};

export default Dropdown;
export type { DropdownOption, DropdownOptionGroup };
